import { useState } from "react";
import { ChevronDown, Plus, Share2, SquarePen, Trash2, Users } from "lucide-react";
import { Button } from "@/v2/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/v2/components/ui/popover";
import { ChannelSwitcherPanel, type ChannelSummary } from "./ChannelSwitcherPanel";
import {
  tenantHoverClassName,
  destructiveHoverClassName,
} from "@/v2/components/tenant-hover";

export function PersonalChannelHeader({
  channelName,
  allChannels,
  currentChannelId,
  onSelectChannel,
  incomingRequestsCount,
  onOpenShares,
  onAddChannel,
  onEditChannel,
  onDeleteChannel,
  onShareChannel,
  showShareAction,
}: {
  channelName: string;
  allChannels: ChannelSummary[];
  currentChannelId: string | number;
  onSelectChannel: (id: string | number) => void;
  incomingRequestsCount: number;
  onOpenShares: () => void;
  onAddChannel: () => void;
  /** Optional id so the switcher rows can act on a channel other than the one
   *  on screen; the header's own buttons call these with no argument. */
  onEditChannel: (channelId?: string | number) => void;
  onDeleteChannel: (channelId?: string | number) => void;
  onShareChannel: (channelId?: string | number) => void;
  showShareAction: boolean;
}) {
  const [channelMenuOpen, setChannelMenuOpen] = useState(false);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-end gap-3 min-w-0">
        <div className="min-w-0">
          <div className="text-[11px] font-bold text-slate-500 uppercase tracking-[0.12em]">
            My Spotlight
          </div>
          <Popover open={channelMenuOpen} onOpenChange={setChannelMenuOpen}>
            <PopoverTrigger asChild>
              {/* Reads as a select: the channel is a value you change, not a
                  page title. The outline is a ring, not a border - v2.css's
                  unlayered `border-width: 0` preflight replacement outranks
                  any border utility on a <button>, so a border here would
                  simply not draw. Rings are box-shadows and are unaffected. */}
              <button
                type="button"
                className="flex items-center justify-between gap-2 rounded-lg bg-white ring-1 ring-slate-200 hover:ring-slate-300 data-[state=open]:ring-2 data-[state=open]:ring-ring transition-shadow"
                style={{ padding: "7px 10px", marginTop: "2px", minWidth: "180px" }}
              >
                <span className="truncate max-w-[220px] font-extrabold text-slate-900 text-base">
                  {channelName}
                </span>
                <ChevronDown className="w-4 h-4 shrink-0 text-slate-500" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-auto p-3">
              <ChannelSwitcherPanel
                channels={allChannels}
                currentChannelId={currentChannelId}
                onSelect={(id) => {
                  setChannelMenuOpen(false);
                  onSelectChannel(id);
                }}
                onAddChannel={() => {
                  setChannelMenuOpen(false);
                  onAddChannel();
                }}
                onShareChannel={(id) => {
                  setChannelMenuOpen(false);
                  onShareChannel(id);
                }}
                onEditChannel={(id) => {
                  setChannelMenuOpen(false);
                  onEditChannel(id);
                }}
                onDeleteChannel={(id) => {
                  setChannelMenuOpen(false);
                  onDeleteChannel(id);
                }}
              />
            </PopoverContent>
          </Popover>
        </div>

        <button
          type="button"
          aria-label="Spotlight sharing"
          onClick={onOpenShares}
          className="relative flex items-center justify-center rounded-full size-8 text-slate-500 hover:bg-accent hover:text-slate-900 shrink-0"
          style={{ marginBottom: "3px" }}
        >
          <Users className="w-4 h-4" />
          {incomingRequestsCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center rounded-full bg-destructive text-white text-[10px] font-bold size-4">
              {incomingRequestsCount}
            </span>
          )}
        </button>
      </div>

      {/* The outline variant's hover is a fixed --v2-accent blue, which
          theme-bridge does not re-theme, so it read as the same colour on every
          tenant. These hover into the tenant's own primary instead. */}
      <div className="flex items-center gap-2 flex-wrap">
        <Button
          variant="outline"
          size="sm"
          className={`gap-1.5 ${tenantHoverClassName}`}
          onClick={onAddChannel}
        >
          <Plus className="w-4 h-4" /> Add Spotlight
        </Button>
        {showShareAction && (
          <Button
            variant="outline"
            size="sm"
            className={`gap-1.5 ${tenantHoverClassName}`}
            onClick={() => onShareChannel()}
          >
            <Share2 className="w-4 h-4" /> Share
          </Button>
        )}
        <Button
          variant="outline"
          size="sm"
          className={`gap-1.5 ${tenantHoverClassName}`}
          onClick={() => onEditChannel()}
        >
          <SquarePen className="w-4 h-4" /> Edit
        </Button>
        <Button
          variant="outline"
          size="icon-sm"
          aria-label="Delete Spotlight"
          className={destructiveHoverClassName}
          onClick={() => onDeleteChannel()}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
